import React, { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import ClientAreaAuditLogModal from 'components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Input } from 'components/Input'
import { MultiSelect, Option, Select } from 'components/Select'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { getWalletCurrenciesAction } from 'store/wallet/wallet-actions'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import { useCurrencyMarkups } from '../hooks/useCurrencyMarkups'
import { EditableHighlightCell } from './EditableHighlightCell'
import { currencyMarkupColumns } from './recordsCurrencyMarkupColumns'

import styles from './../CurrencyMarkups.module.scss'

const RecordsCurrencyMarkups: React.FC = () => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [quoteCurrency, setQuoteCurrency] = useState('')
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [baseCurrencies, setBaseCurrencies] = useState<string[]>([])
  const [withdrawalPercentageInput, setWithdrawalPercentageInput] = useState('0')
  const [auditLogModal, setAuditLogModal] = useState(false)
  const withdrawalPercentage = Number(withdrawalPercentageInput) || 0

  const {
    hasChanges,
    handleItemChange,
    handleCellClick,
    handleSubmit,
    onClickFetch,
    editableRates,
    invalidRateMessage,
  } = useCurrencyMarkups(quoteCurrency, baseCurrencies, withdrawalPercentage)

  const EditableCellWithClick = (props: GridCellProps) => (
    <EditableHighlightCell {...props} onCellClick={handleCellClick} />
  )

  const currencyList = useAppSelector((state) => state.wallet.currencies)
  const currencies = currencyList.filter((currency) => currency.id !== 'N/A')
  const dispatch = useAppDispatch()
  const { gridState } = useGridState()
  const excelExportRef = React.useRef<ExcelExport>(null)

  useEffect(() => {
    if (isEmpty(currencies)) {
      dispatch(getWalletCurrenciesAction(walletsApiClient))
    }
  }, [])

  return (
    <>
      <Breadcrumbs
        crumbs={[{ title: 'Currency Markups', link: links.recordsCurrencyMarkups.url }]}
      />

      {auditLogModal && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal}
          onClose={() => setAuditLogModal(false)}
          isCurrencyMarkup
        />
      )}

      <ConfirmationModal
        visible={openConfirmationModal}
        onYes={() => {
          handleSubmit()
          setOpenConfirmationModal(false)
        }}
        onCancel={() => setOpenConfirmationModal(false)}
        contentText='Are you sure you want to update the markup rates?'
        yesButtonText='Confirm'
      />

      <div className={styles.quoteCurrency}>
        <label>Quote Currency</label>
        <Select
          placeholder='Select quote currency'
          className={styles.selectButtons}
          showSearch={true}
          onChange={setQuoteCurrency}
        >
          {currencies
            .filter((currency) => !baseCurrencies.includes(currency.id))
            .map((currency) => (
              <Option key={currency.id} value={currency.id}>
                {currency.id}
              </Option>
            ))}
        </Select>
      </div>

      <div className={styles.baseCurrency}>
        <label>Base Currency</label>
        <MultiSelect
          placeholder='Select base currency'
          className={styles.selectButtons}
          showSearch
          onChange={setBaseCurrencies}
        >
          {currencies
            .filter((currency) => currency.id !== quoteCurrency)
            .map((currency) => (
              <Option key={currency.id} value={currency.id}>
                {currency.id}
              </Option>
            ))}
        </MultiSelect>
      </div>

      <div className={styles.withdrawalPercentageWrapper}>
        <div className={styles.withdrawalPercentage}>
          <label>Applied withdrawal percentage</label>
          <Input
            min={-100}
            max={100}
            value={withdrawalPercentageInput}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || value === '-') {
                setWithdrawalPercentageInput(value)
              } else {
                const numValue = Number(value)
                if (!isNaN(numValue) && numValue >= -100 && numValue <= 100) {
                  setWithdrawalPercentageInput(value)
                }
              }
            }}
            onBlur={() => {
              if (withdrawalPercentageInput === '' || withdrawalPercentageInput === '-') {
                setWithdrawalPercentageInput('0')
              }
            }}
            type='number'
            className={styles.percentageBtn}
          ></Input>
        </div>
        <label>%</label>
      </div>

      <div className={styles.actionBtns}>
        <Button
          type='button'
          disabled={!baseCurrencies.length || !quoteCurrency}
          onClick={() => onClickFetch(quoteCurrency)}
        >
          Get Rates
        </Button>
        <div className={styles.actionBtnsExport}>
          <ButtonGroup>
            <Button type='button' variant='outline' onClick={() => excelExportRef.current?.save()}>
              Export to Excel
            </Button>
            <Button type='button' variant='outline' onClick={() => setAuditLogModal(true)}>
              Audit Log
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className={styles.currencyRatesTable}>
        <ExcelExport ref={excelExportRef} fileName='CurrencyMarkups.xlsx' data={editableRates}>
          <Grid
            {...gridState}
            data={editableRates}
            editField='inEdit'
            onItemChange={handleItemChange}
            resizable
            pageable
            sortable
          >
            {currencyMarkupColumns
              .filter((col) => col.show !== false)
              .map((col, idx) => (
                <Column
                  key={idx}
                  field={col.field}
                  title={col.title}
                  editable={col.editable}
                  filterable={col.filterable}
                  sortable={col.sortable}
                  width={col.width}
                  className={col.className}
                  editor={col.editor}
                  format={col.format}
                  cell={col.editable ? EditableCellWithClick : undefined}
                />
              ))}
          </Grid>

          <div className={styles.updateBtn}>
            <Button
              type='button'
              onClick={() => setOpenConfirmationModal(true)}
              disabled={!hasChanges() || invalidRateMessage.length !== 0}
            >
              Update Rates
            </Button>
          </div>
        </ExcelExport>
      </div>
    </>
  )
}

export default RecordsCurrencyMarkups
